import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import clinicAPI from "../../../services/clinicApi";
import { usePatientsBase } from "../../../utils/hubPaths";
import s from "../clinic.module.css";
import aa from "../AdminPortal/adminApp.module.css";
import { Button, Field, Input, PhoneInput, useToast } from "../../ui";

const STATUS_LABELS = {
  pending: "Без входа в кабинет",
  sent: "Приглашение отправлено",
  registered: "Вход активен",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function emptyForm() {
  return {
    contact_name: "",
    contact_phone: "",
    contact_email: "",
    relation: "",
  };
}

function guardianStatusLabel(g) {
  if (g.portal_activated) return "Вход активен";
  const linked = Boolean(g.guardian_user_id || g.guardian?.id);
  // Старый статус pending раньше показывали как «Не привязан» — путали с отсутствием связи
  if (g.invite_status === "pending" && linked) {
    return g.contact_email || g.guardian?.email
      ? "Без входа в кабинет"
      : "Без email — вход недоступен";
  }
  if (g.invite_status === "pending" && !linked) {
    return "Не привязан к аккаунту";
  }
  return STATUS_LABELS[g.invite_status] || g.invite_status;
}

function canInviteGuardian(g) {
  return !g.portal_activated;
}

function guardianProfileId(g) {
  return g.guardian_user_id || g.guardian?.id || null;
}

export default function AdminGuardiansPanel({
  patientId,
  onChanged,
  compact = false,
  layout = "compact",
}) {
  const toast = useToast();
  const patientsBase = usePatientsBase();
  const [guardians, setGuardians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const data = await clinicAPI.getAthleteGuardians(patientId);
      setGuardians(data.guardians || []);
    } catch (e) {
      toast.error(e.response?.data?.error || "Не удалось загрузить родителей");
      setGuardians([]);
    } finally {
      setLoading(false);
    }
  }, [patientId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const addGuardian = async (e) => {
    e.preventDefault();
    if (!form.contact_name.trim()) {
      toast.error("Укажите ФИО родителя");
      return;
    }
    const email = form.contact_email.trim().toLowerCase();
    if (!email) {
      toast.error("Укажите email родителя");
      return;
    }
    if (!EMAIL_RE.test(email)) {
      toast.error("Некорректный email");
      return;
    }
    setSaving(true);
    try {
      await clinicAPI.addAthleteGuardian(patientId, {
        contact_name: form.contact_name.trim(),
        contact_phone: form.contact_phone.trim() || undefined,
        contact_email: email,
        relation: form.relation.trim() || undefined,
        is_primary: guardians.length === 0,
      });
      toast.success("Родитель добавлен");
      setForm(emptyForm());
      setShowForm(false);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка");
    } finally {
      setSaving(false);
    }
  };

  const sendInvite = async (guardian) => {
    if (!canInviteGuardian(guardian)) {
      toast.info("Родитель уже активировал вход");
      return;
    }
    setBusyId(guardian.id);
    try {
      const r = await clinicAPI.sendAthleteGuardianInvite(patientId, guardian.id);
      if (r.email_sent) {
        toast.success("Приглашение отправлено на email");
      } else if (r.invite_url) {
        await navigator.clipboard.writeText(r.invite_url);
        toast.success("Ссылка скопирована — отправьте родителю");
      } else {
        toast.success("Приглашение создано");
      }
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось отправить");
    } finally {
      setBusyId(null);
    }
  };

  const removeGuardian = async (guardian) => {
    if (!window.confirm(`Убрать ${guardian.contact_name} из ответственных?`)) return;
    setBusyId(guardian.id);
    try {
      await clinicAPI.removeAthleteGuardian(patientId, guardian.id);
      toast.success("Связь удалена");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка");
    } finally {
      setBusyId(null);
    }
  };

  const renderRow = (g) => {
    const meta = [g.relation, g.contact_phone, g.contact_email].filter(Boolean).join(" · ");
    const status = guardianStatusLabel(g);
    const profileId = guardianProfileId(g);
    const profileHref = profileId ? `${patientsBase}/${profileId}` : null;
    const showInvite = canInviteGuardian(g);

    const mainContent = (
      <>
        <strong>{g.contact_name}</strong>
        <span>{meta || status}</span>
        {meta ? <small>{status}</small> : null}
      </>
    );

    if (compact) {
      return (
        <li key={g.id} className={aa.clientGuardiansCompactRow}>
          {profileHref ? (
            <Link to={profileHref} className={aa.clientGuardiansCompactRowMain}>
              {mainContent}
            </Link>
          ) : (
            <span className={aa.clientGuardiansCompactRowMain}>{mainContent}</span>
          )}
          <div className={aa.clientGuardiansCompactActions}>
            {showInvite ? (
              <button
                type="button"
                className={aa.clientGuardiansCompactLink}
                disabled={busyId === g.id}
                onClick={() => sendInvite(g)}
              >
                {busyId === g.id ? "…" : "Пригласить"}
              </button>
            ) : null}
            <button
              type="button"
              className={aa.clientGuardiansCompactLinkDanger}
              disabled={busyId === g.id}
              onClick={() => removeGuardian(g)}
            >
              Убрать
            </button>
          </div>
        </li>
      );
    }

    return (
      <li key={g.id} className={s.guardianRow}>
        <div className={s.guardianRowMain}>
          {profileHref ? (
            <Link to={profileHref} className={s.guardianNameLink}>
              {g.contact_name}
            </Link>
          ) : (
            <strong>{g.contact_name}</strong>
          )}
          <span className={s.guardianStatus}>{status}</span>
          {g.relation ? <span className={s.guardianMeta}>{g.relation}</span> : null}
          {g.contact_phone ? (
            <a href={`tel:${g.contact_phone}`} className={s.guardianMeta}>
              {g.contact_phone}
            </a>
          ) : null}
          {g.contact_email ? (
            <span className={s.guardianMeta}>{g.contact_email}</span>
          ) : null}
        </div>
        <div className={s.guardianRowActions}>
          {showInvite ? (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={busyId === g.id}
              onClick={() => sendInvite(g)}
            >
              {busyId === g.id ? "…" : "Пригласить"}
            </Button>
          ) : null}
          <button
            type="button"
            className={s.guardianRemoveBtn}
            disabled={busyId === g.id}
            onClick={() => removeGuardian(g)}
          >
            Убрать
          </button>
        </div>
      </li>
    );
  };

  const formBlock = showForm ? (
    <form
      className={compact ? aa.clientGuardiansCompactForm : s.clientTabFormStack}
      onSubmit={addGuardian}
    >
      <Field label="ФИО родителя *">
        <Input
          value={form.contact_name}
          onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
          autoFocus
        />
      </Field>
      <Field label="Email *">
        <Input
          type="email"
          value={form.contact_email}
          onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
          required
        />
      </Field>
      <Field label="Телефон">
        <PhoneInput
          value={form.contact_phone || ""}
          onChange={(v) => setForm({ ...form, contact_phone: v })}
        />
      </Field>
      <Field label="Кем приходится">
        <Input
          value={form.relation}
          onChange={(e) => setForm({ ...form, relation: e.target.value })}
          placeholder="Мама, папа…"
        />
      </Field>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Button type="submit" size="sm" disabled={saving}>
          {saving ? "Сохранение…" : "Сохранить"}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => {
            setShowForm(false);
            setForm(emptyForm());
          }}
        >
          Отмена
        </Button>
      </div>
    </form>
  ) : null;

  if (compact) {
    const compactClass =
      layout === "desktop"
        ? `${aa.clientGuardiansCompact} ${aa.clientGuardiansDesktop}`
        : aa.clientGuardiansCompact;

    return (
      <section className={compactClass} aria-label="Родители">
        <div className={aa.clientGuardiansCompactHead}>
          <span className={aa.clientGuardiansCompactLabel}>Родители</span>
          {!showForm ? (
            <button
              type="button"
              className={aa.clientGuardiansCompactAdd}
              onClick={() => setShowForm(true)}
            >
              + Добавить
            </button>
          ) : null}
        </div>

        {loading ? (
          <p className={aa.clientGuardiansCompactEmpty}>Загрузка…</p>
        ) : guardians.length > 0 ? (
          <ul className={aa.clientGuardiansCompactList}>{guardians.map(renderRow)}</ul>
        ) : !showForm ? (
          <p className={aa.clientGuardiansCompactEmpty}>Родители не указаны</p>
        ) : null}

        {formBlock}
      </section>
    );
  }

  return (
    <section className={s.clientTabCard}>
      <div className={s.summaryBlockHead}>
        <h3 className={s.clientTabCardTitle}>Родители / ответственные</h3>
        {!showForm ? (
          <Button type="button" size="sm" onClick={() => setShowForm(true)}>
            + Добавить
          </Button>
        ) : null}
      </div>

      {loading ? (
        <p className={s.summaryEmptyNote}>Загрузка…</p>
      ) : guardians.length === 0 && !showForm ? (
        <p className={s.summaryEmptyNote}>Родители не указаны</p>
      ) : (
        <ul className={s.guardianList}>{guardians.map(renderRow)}</ul>
      )}

      {formBlock}
    </section>
  );
}
